/**
 * 공통 Auth API 서비스
 * AppProvider와 AdminAppProvider에서 중복되던 API 호출 로직을 통합
 * 
 * 설계 원칙:
 * - /api/auth/user와 /api/auth/logout API 호출을 단일화
 * - 일관된 에러 처리 및 로깅 적용
 * - 재사용 가능한 공통 함수로 DRY 원칙 준수
 */

import { api } from '@/lib/api';
import { authLogger } from '@/lib/logger';
import type { User } from '@/types';

// 공통 응답 타입
export interface AuthUserResponse {
    success: boolean;
    user: User | null;
    error?: string;
}

export interface AuthLogoutResponse {
    success: boolean;
    error?: string;
}

/**
 * 현재 사용자 정보 조회 공통 함수
 * AppProvider와 AdminAppProvider 모두에서 사용
 */
export async function getCurrentUser(): Promise<AuthUserResponse> {
    authLogger.log('🔍 사용자 정보 조회 시작');
    
    try {
        const response = await api.get('/api/auth/user');
        
        if (response.success && response.data) {
            const userData = response.data.user || response.data;
            
            if (userData) {
                authLogger.log('✅ 사용자 정보 조회 성공', {
                    email: userData.email,
                    roles: userData.roles || []
                });
                
                return {
                    success: true,
                    user: userData
                };
            }
        }
        
        authLogger.warn('⚠️ 사용자 데이터 없음');
        return {
            success: false,
            user: null,
            error: '사용자 정보를 찾을 수 없습니다.'
        };
    } catch (error) {
        authLogger.error('❌ 사용자 정보 조회 실패', error);
        return {
            success: false,
            user: null,
            error: error instanceof Error ? error.message : '사용자 인증 확인 실패'
        };
    }
}

/**
 * 로그아웃 공통 함수
 * AppProvider와 AdminAppProvider 모두에서 사용
 */
export async function logout(): Promise<AuthLogoutResponse> {
    authLogger.log('🚪 로그아웃 API 호출 시작');
    
    try {
        await api.post('/api/auth/logout', {});
        
        authLogger.log('✅ 로그아웃 API 호출 성공');
        return {
            success: true
        };
    } catch (error) {
        authLogger.error('❌ 로그아웃 API 호출 실패', error);
        return {
            success: false,
            error: error instanceof Error ? error.message : '로그아웃 중 오류가 발생했습니다.'
        };
    }
}

/**
 * 관리자 권한 확인 헬퍼 함수
 * AdminAppProvider에서 사용
 */
export function checkAdminRoles(roles: string[] = []): boolean {
    const adminRoles = ['admin', 'moderator', 'developer'];
    return roles.some(role => adminRoles.includes(role));
}

/**
 * 사용자 데이터 처리 공통 함수
 * AppProvider 방식과 일치하게 avatar → photoURL 변환
 */
export function processUserData(userData: any): User {
    return {
        ...userData,
        photoURL: userData.avatar || undefined // Laravel avatar → photoURL로 변환
    };
}